import React, { useState, useEffect, useCallback, useRef } from 'react';
import useMobileExpand from '../hooks/useMobileExpand';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6'];

/* ── Chart Renderer (larger) ── */
function ChartRenderer({ chart, chartRef }) {
  if (!chart || !chart.data || !chart.type) return null;
  const { type, title, data, xKey, yKeys: rawYKeys, dataKey, nameKey } = chart;
  const yKeys = (rawYKeys || []).map(k => typeof k === 'string' ? k : k.key);

  const wrap = (children) => (
    <div ref={chartRef} style={{ marginTop: 10 }}>
      {title && <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: 8, textAlign: 'center' }}>{title}</div>}
      <ResponsiveContainer width="100%" height={340}>{children}</ResponsiveContainer>
    </div>
  );

  if (type === 'bar' && xKey && yKeys.length) {
    return wrap(
      <BarChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {yKeys.map((k, i) => <Bar key={k} dataKey={k} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />)}
      </BarChart>
    );
  }

  if (type === 'line' && xKey && yKeys.length) {
    return wrap(
      <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {yKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }} />)}
      </LineChart>
    );
  }

  if (type === 'area' && xKey && yKeys.length) {
    return wrap(
      <AreaChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {yKeys.map((k, i) => <Area key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.15} strokeWidth={2.5} />)}
      </AreaChart>
    );
  }

  if (type === 'pie' && dataKey && nameKey) {
    return wrap(
      <PieChart>
        <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    );
  }

  return null;
}

/* ── PDF Export ──
   Rasterises the chart element (SVG + legend wrapper) via html2canvas and embeds
   the image into a jsPDF document. Direct download — no print dialog, no popup
   blocker risk. Consistent with the rest of the app's report exports. */
async function exportChartToPdf(chartEl, title) {
  if (!chartEl) return;
  try {
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);
    const canvas = await html2canvas(chartEl, {
      backgroundColor: '#ffffff',
      scale: 2, // higher DPI so the chart stays sharp in the PDF
      useCORS: true,
      logging: false,
    });
    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(51, 65, 85);
    doc.text(String(title || 'AI Assistant Chart'), margin, margin + 6);
    // Chart image — fit inside page, preserve aspect ratio
    const availW = pageWidth - margin * 2;
    const availH = pageHeight - margin * 2 - 40; // leave room for title + footer
    const ratio = canvas.width / canvas.height;
    let drawW = availW;
    let drawH = drawW / ratio;
    if (drawH > availH) {
      drawH = availH;
      drawW = drawH * ratio;
    }
    const x = margin + (availW - drawW) / 2;
    const y = margin + 20;
    doc.addImage(imgData, 'PNG', x, y, drawW, drawH);
    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Generated by EASEPMS AI Staff Assistant — ${new Date().toLocaleString()}`,
      margin,
      pageHeight - 18
    );
    const safe = String(title || 'AI_Chart').replace(/[^a-z0-9_-]/gi, '_').slice(0, 60);
    doc.save(`${safe}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('Chart PDF export failed', err);
  }
}

/* ── Draggable Floating Icon ── */
function AiFloatingWidget({ token, selectedProperty }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [outputMode, setOutputMode] = useState('graph'); // 'text' or 'graph'
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const { showLabel, handleTap } = useMobileExpand();
  const chatEndRef = useRef(null);
  const chartRefs = useRef({});

  // Draggable state
  const [pos, setPos] = useState({ x: null, y: null });
  const dragRef = useRef(null);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  useEffect(() => {
    setPos({ x: window.innerWidth - 165, y: window.innerHeight - 72 });
  }, []);

  const dragStartPos = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5;

  const onMouseDown = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      if (!hasMoved.current && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      hasMoved.current = true;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 56, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 56, e.clientY - dragOffset.current.y)),
      });
    };
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, []);

  const onTouchStart = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    const touch = e.touches[0];
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
  };

  useEffect(() => {
    const onTouchMove = (e) => {
      if (!dragging.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragStartPos.current.x;
      const dy = touch.clientY - dragStartPos.current.y;
      if (!hasMoved.current && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      hasMoved.current = true;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 56, touch.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 56, touch.clientY - dragOffset.current.y)),
      });
    };
    const onTouchEnd = () => { dragging.current = false; };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    return () => { window.removeEventListener('touchmove', onTouchMove); window.removeEventListener('touchend', onTouchEnd); };
  }, []);

  const handleIconClick = () => {
    if (hasMoved.current) return;
    if (handleTap()) setOpen(prev => !prev);
  };

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  useEffect(() => {
    if (!open || historyLoaded || !selectedProperty?.id) return;
    (async () => {
      try {
        const res = await fetch('/api/v1/ai/staff-assistant/history?limit=30', { headers: headers() });
        const data = await res.json();
        if (res.ok && data.messages) {
          setMessages(data.messages.map(m => {
            let parsed = {};
            try { parsed = JSON.parse(m.ai_response); } catch (_) { /* ignore */ }
            return [
              { role: 'user', text: m.user_message, time: m.created_at },
              {
                role: 'ai', text: parsed.reply || m.ai_response, intent: m.detected_intent, time: m.created_at,
                chart: parsed.chart || null, viewMode: parsed.chart ? 'chart' : 'text',
              },
            ];
          }).flat());
        }
      } catch (_) { /* ignore */ }
      setHistoryLoaded(true);
    })();
  }, [open, historyLoaded, selectedProperty?.id, headers]);

  useEffect(() => {
    setMessages([]);
    setHistoryLoaded(false);
  }, [selectedProperty?.id]);

  useEffect(() => { if (open) chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const toggleView = (idx) => {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, viewMode: m.viewMode === 'text' ? 'chart' : 'text' } : m));
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || !selectedProperty?.id) return;
    setInput('');
    setError('');
    setMessages(prev => [...prev, { role: 'user', text: msg, time: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await fetch('/api/v1/ai/staff-assistant/chat', {
        method: 'POST', headers: headers(true), body: JSON.stringify({ message: msg }),
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) throw new Error('Server unavailable');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      const hasChart = data.chart && data.chart.data && data.chart.type;
      setMessages(prev => [...prev, {
        role: 'ai', text: data.reply, intent: data.intent, data: data.data,
        chart: data.chart || null,
        viewMode: hasChart ? (outputMode === 'graph' ? 'chart' : 'text') : 'text',
        time: new Date().toISOString(),
      }]);
    } catch (err) {
      setError(err.message);
      setMessages(prev => [...prev, { role: 'ai', text: `Error: ${err.message}`, time: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const quickChips = ['Occupancy today', 'Revenue this week', 'Open HK tasks', 'Tomorrow departures', 'ADR this month'];

  if (pos.x === null) return null;

  return (
    <>
      {/* ── Floating Draggable Button ── */}
      <div
        ref={dragRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onClick={handleIconClick}
        style={{
          position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999,
          height: 40, borderRadius: 20, paddingLeft: 12, paddingRight: showLabel ? 14 : 12, minWidth: showLabel ? 140 : 40, justifyContent: 'center',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          boxShadow: '0 4px 20px rgba(99,102,241,0.45)',
          display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          cursor: dragging.current ? 'grabbing' : 'grab',
          userSelect: 'none', transition: 'all 0.25s ease',
        }}
        title="AI Insights"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a8 8 0 0 1 8 8c0 3.5-2 6-4 7.5V20a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-2.5C6 16 4 13.5 4 10a8 8 0 0 1 8-8z"/>
          <circle cx="10" cy="10" r="1" fill="#fff"/><circle cx="14" cy="10" r="1" fill="#fff"/>
        </svg>
        {showLabel && <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.03em' }}>AI Insights</span>}
      </div>

      {/* ── Modal Popup ── */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          padding: window.innerWidth <= 600 ? 0 : '1.5rem',
        }} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={{
            width: '100%', maxWidth: window.innerWidth <= 600 ? '100%' : 760,
            height: window.innerWidth <= 600 ? '100%' : undefined,
            maxHeight: window.innerWidth <= 600 ? '100%' : '90vh',
            background: '#fff', borderRadius: window.innerWidth <= 600 ? 0 : 16,
            boxShadow: '0 25px 80px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }} onClick={(e) => e.stopPropagation()}>

            {/* ── Header ── */}
            <div style={{
              padding: '1rem 1.5rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a8 8 0 0 1 8 8c0 3.5-2 6-4 7.5V20a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-2.5C6 16 4 13.5 4 10a8 8 0 0 1 8-8z"/>
                  <circle cx="10" cy="10" r="1" fill="#fff"/><circle cx="14" cy="10" r="1" fill="#fff"/>
                </svg>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>AI Insights</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                    {selectedProperty ? `${selectedProperty.code} \u2014 ${selectedProperty.name}` : ''}
                  </div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
                color: '#fff', cursor: 'pointer', padding: '6px 10px', fontSize: '1.1rem', lineHeight: 1,
              }}>{'\u2715'}</button>
            </div>

            {!selectedProperty?.id ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '1rem', padding: '3rem' }}>
                Please select a property first.
              </div>
            ) : (
              <>
                {/* ── Output Mode Toggle ── */}
                <div style={{ padding: '1rem 1.5rem 0.5rem' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 8 }}>Output Mode</div>
                  <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '2px solid #6366f1' }}>
                    <button onClick={() => setOutputMode('text')}
                      style={{
                        flex: 1, padding: '0.55rem 0', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
                        background: outputMode === 'text' ? '#6366f1' : '#fff',
                        color: outputMode === 'text' ? '#fff' : '#6366f1',
                        transition: 'all 0.15s',
                      }}>
                      {'\u2630'} Text
                    </button>
                    <button onClick={() => setOutputMode('graph')}
                      style={{
                        flex: 1, padding: '0.55rem 0', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
                        background: outputMode === 'graph' ? '#6366f1' : '#fff',
                        color: outputMode === 'graph' ? '#fff' : '#6366f1',
                        borderLeft: '2px solid #6366f1',
                        transition: 'all 0.15s',
                      }}>
                      {'\uD83D\uDCCA'} Graph
                    </button>
                  </div>
                </div>

                {/* ── Tip ── */}
                <div style={{ padding: '0.4rem 1.5rem', fontSize: '0.78rem', color: '#92400e', background: '#fef3c7', margin: '0 1.5rem', borderRadius: 6, marginTop: 8 }}>
                  {'\uD83D\uDCA1'} Tip: Use simple queries for best results. E.g. <i>'Occupancy today'</i> or <i>'Daily revenue this month'</i>.
                </div>

                {/* ── Chat / Results Area ── */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem', background: '#fafbfc', minHeight: 200, maxHeight: '45vh' }}>
                  {messages.length === 0 && !sending && (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2.5rem 0' }}>
                      <div style={{ fontSize: '2.2rem', marginBottom: '0.6rem' }}>{'\uD83E\uDD16'}</div>
                      <div style={{ fontSize: '1rem', fontWeight: 600 }}>How can I help?</div>
                      <div style={{ fontSize: '0.85rem', marginTop: 6, color: '#b0b8c4' }}>Ask about occupancy, revenue, guests, or any hotel operations.</div>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '0.7rem' }}>
                      <div style={{
                        maxWidth: '100%', width: m.chart && m.viewMode === 'chart' ? '100%' : undefined,
                        padding: m.chart && m.viewMode === 'chart' ? '0.8rem 1rem' : '0.6rem 1rem',
                        borderRadius: 12,
                        background: m.role === 'user' ? '#6366f1' : '#fff',
                        color: m.role === 'user' ? '#fff' : '#1e293b',
                        border: m.role === 'ai' ? '1px solid #e5e7eb' : 'none',
                        fontSize: '0.9rem', lineHeight: 1.6,
                        boxShadow: m.role === 'ai' ? '0 2px 6px rgba(0,0,0,0.05)' : 'none',
                      }}>
                        {/* View toggle + PDF export */}
                        {m.chart && m.role === 'ai' && (
                          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                            <button onClick={() => toggleView(i)}
                              style={{
                                padding: '4px 14px', borderRadius: 6, border: '1px solid #e2e8f0',
                                background: m.viewMode === 'chart' ? '#6366f1' : '#fff',
                                color: m.viewMode === 'chart' ? '#fff' : '#475569',
                                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                              }}>Chart</button>
                            <button onClick={() => toggleView(i)}
                              style={{
                                padding: '4px 14px', borderRadius: 6, border: '1px solid #e2e8f0',
                                background: m.viewMode === 'text' ? '#6366f1' : '#fff',
                                color: m.viewMode === 'text' ? '#fff' : '#475569',
                                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                              }}>Text</button>
                            {m.viewMode === 'chart' && (
                              <button onClick={() => exportChartToPdf(chartRefs.current[i], m.chart?.title)}
                                title="Export chart as PDF"
                                style={{
                                  marginLeft: 'auto', padding: '4px 14px', borderRadius: 6,
                                  border: '1px solid #fca5a5', background: '#fff', color: '#ef4444',
                                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                                  display: 'flex', alignItems: 'center', gap: 5,
                                }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v4a1 1 0 001 1h4"/><path d="M17 21H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z"/><path d="M12 17v-6"/><path d="M9.5 14.5L12 17l2.5-2.5"/></svg>
                                PDF
                              </button>
                            )}
                          </div>
                        )}

                        {m.chart && m.viewMode === 'chart' && (
                          <ChartRenderer chart={m.chart} chartRef={(el) => { chartRefs.current[i] = el; }} />
                        )}

                        {(!m.chart || m.viewMode === 'text') && (
                          <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
                        )}

                        {m.intent && m.role === 'ai' && (
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 6 }}>{m.intent}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.7rem' }}>
                      <div style={{ padding: '0.6rem 1rem', borderRadius: 12, background: '#fff', border: '1px solid #e5e7eb', color: '#94a3b8', fontSize: '0.9rem' }}>
                        <span className="ai-thinking-dots">Thinking</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {error && <div style={{ padding: '0.5rem 1.5rem', fontSize: '0.85rem', color: '#ef4444', background: '#fef2f2', borderTop: '1px solid #fecaca' }}>{error}</div>}

                {/* ── Input Area ── */}
                <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 8 }}>What would you like to know?</div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                      placeholder="Ask about occupancy, revenue, guests..."
                      disabled={sending}
                      style={{
                        flex: 1, padding: '0.7rem 1rem', border: '2px solid #e2e8f0',
                        borderRadius: 10, fontSize: '0.95rem', outline: 'none',
                        transition: 'border-color 0.15s',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                    <button onClick={() => sendMessage()} disabled={sending || !input.trim()}
                      style={{
                        padding: '0.7rem 1.5rem', borderRadius: 10, border: 'none',
                        background: sending || !input.trim() ? '#cbd5e1' : '#6366f1',
                        color: '#fff', fontWeight: 700, cursor: sending ? 'wait' : 'pointer',
                        fontSize: '0.95rem', transition: 'background 0.15s',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                      {'\u2708'} Send
                    </button>
                  </div>

                  {/* Try asking chips */}
                  <div style={{ marginTop: 10 }}>
                    <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500 }}>Try asking: </span>
                    {quickChips.map(c => (
                      <button key={c} onClick={() => sendMessage(c)} disabled={sending}
                        style={{
                          padding: '0.2rem 0.65rem', borderRadius: 14, border: '1px solid #e2e8f0',
                          background: '#f8fafc', color: '#6366f1', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600,
                          marginRight: 5, marginTop: 4, transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.target.style.background = '#6366f1'; e.target.style.color = '#fff'; e.target.style.borderColor = '#6366f1'; }}
                        onMouseLeave={(e) => { e.target.style.background = '#f8fafc'; e.target.style.color = '#6366f1'; e.target.style.borderColor = '#e2e8f0'; }}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes aiDots { 0%, 20% { content: '.'; } 40% { content: '..'; } 60%, 100% { content: '...'; } }
        .ai-thinking-dots::after { content: ''; animation: aiDots 1.2s infinite steps(1); }
      `}</style>
    </>
  );
}

export default AiFloatingWidget;
